'use client'

import { useEffect } from 'react'

/**
 * Patches Node.prototype.removeChild and insertBefore to catch NotFoundError
 * and no-op instead of throwing. Prevents app crashes when:
 * - React tries to remove a node that was already moved/removed by a browser extension (e.g. translation).
 * - Slow networks / hydration timing cause DOM and React tree to diverge (e.g. Colombia, mobile).
 *
 * See: client_error "Failed to execute 'removeChild' on 'Node'" on /es/signup, etc.
 * Only runs in the browser and only applies the patch once.
 */
export function DomSafetyPatch() {
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return

        const NativeNode = window.Node.prototype

        if ((NativeNode as unknown as { __removeChildPatched?: boolean }).__removeChildPatched) {
            return
        }

        const originalRemoveChild = NativeNode.removeChild
        NativeNode.removeChild = function removeChild<T extends Node>(child: T): T {
            try {
                return originalRemoveChild.call(this, child) as T
            } catch (e) {
                const err = e as Error & { name?: string; code?: number }
                if (err?.name === 'NotFoundError' || err?.code === 8) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('[DomSafetyPatch] removeChild NotFoundError suppressed (extension/slow network):', e)
                    }
                    return child
                }
                throw e
            }
        }
        ;(NativeNode as unknown as { __removeChildPatched?: boolean }).__removeChildPatched = true

        const originalInsertBefore = NativeNode.insertBefore
        NativeNode.insertBefore = function insertBefore<T extends Node>(newNode: T, refNode: Node | null): T {
            try {
                return originalInsertBefore.call(this, newNode, refNode) as T
            } catch (e) {
                const err = e as Error & { name?: string; code?: number }
                if (err?.name === 'NotFoundError' || err?.code === 8) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('[DomSafetyPatch] insertBefore NotFoundError suppressed (extension/slow network):', e)
                    }
                    return newNode
                }
                throw e
            }
        }
        ;(NativeNode as unknown as { __insertBeforePatched?: boolean }).__insertBeforePatched = true
    }, [])

    return null
}
